import { db } from '../firebase';
import { getCampaignSearchFields } from '../utils/campaignSearchUtils';
import { 
  collection, 
  doc, 
  getDoc,
  addDoc, 
  serverTimestamp
} from 'firebase/firestore';

/**
 * Service for campaign operations
 */

/**
 * Duplicate a template campaign into user's account (no ownership check)
 * @param {string} templateCampaignId - The ID of the template campaign to copy
 * @param {string} targetCompanyId - The company ID to assign the new campaign to
 * @param {string} newName - Optional name for the new campaign
 * @returns {Promise<{id: string, name: string}>} - The new campaign ID and name
 */
export async function duplicateFromTemplate(templateCampaignId, targetCompanyId, newName) {
  try {
    const campaignRef = doc(db, 'campaign', templateCampaignId);
    const campaignSnap = await getDoc(campaignRef);
    
    if (!campaignSnap.exists()) {
      throw new Error('Template campaign not found');
    }
    
    const originalData = campaignSnap.data();
    const originalInternalName = newName || originalData.internalName || originalData.options?.title || 'New Game';
    let newInternalName;
    if (newName) {
      newInternalName = newName;
    } else {
      const baseName = originalInternalName.replace(/\s*\((?:Copy(?:\s*\d+)?|[A-Z][a-z]{2}\s+\d{1,2})\)\s*$/i, '').trim() || originalInternalName;
      const stamp = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric' });
      newInternalName = `${baseName} (${stamp})`;
    }
    
    const baseData = {
      ...originalData,
      internalName: newInternalName,
      name: newInternalName,
      options: { ...originalData.options },
      status: 'draft',
      createdAt: serverTimestamp(),
      hasBeenPlayed: false,
      companyId: targetCompanyId,
      duplicatedFrom: templateCampaignId,
      duplicatedAt: serverTimestamp(),
      isFromTemplate: true
    };
    const { _searchInternalName, _searchTitle } = getCampaignSearchFields(baseData);
    const duplicatedData = { ...baseData, _searchInternalName, _searchTitle };

    delete duplicatedData.goLiveDate;
    delete duplicatedData.completeDate;
    delete duplicatedData.publishedAt;
    delete duplicatedData.completedAt;
    delete duplicatedData.pausedAt;
    delete duplicatedData.archivedAt;
    delete duplicatedData.paidAt;
    delete duplicatedData.stripeSessionId;
    
    const newCampaignRef = await addDoc(collection(db, 'campaign'), duplicatedData);
    
    console.log(`✅ Template duplicated: ${templateCampaignId} → ${newCampaignRef.id}`);
    
    return {
      id: newCampaignRef.id,
      name: newInternalName
    };
  } catch (error) {
    console.error('Error duplicating template:', error);
    throw error;
  }
}

/**
 * Duplicate a campaign (create a copy with new ID, QR code, and leaderboard)
 * @param {string} campaignId - The ID of the campaign to duplicate
 * @param {string} companyId - The company ID for ownership verification
 * @returns {Promise<{id: string, name: string}>} - The new campaign ID and name
 */
export async function duplicateCampaign(campaignId, companyId) {
  try {
    // Fetch the original campaign
    const campaignRef = doc(db, 'campaign', campaignId);
    const campaignSnap = await getDoc(campaignRef);
    
    if (!campaignSnap.exists()) {
      throw new Error('Campaign not found');
    }
    
    const originalData = campaignSnap.data();
    
    // Verify ownership
    if (originalData.companyId !== companyId) {
      throw new Error('You do not have permission to duplicate this campaign');
    }
    
    // Get the original internal name (or fall back to public title)
    const originalInternalName = originalData.internalName || originalData.options?.title || originalData.name || 'Untitled Game';
    // Strip any prior " (Copy)" / " (Copy 2)" / " (Mar 5)" suffix so we don't stack them
    const baseName = originalInternalName.replace(/\s*\((?:Copy(?:\s*\d+)?|[A-Z][a-z]{2}\s+\d{1,2})\)\s*$/i, '').trim() || originalInternalName;
    const stamp = new Date().toLocaleString('en-US', { month: 'short', day: 'numeric' });
    const newInternalName = `${baseName} (${stamp})`;
    
    // Create the duplicated campaign data (new copy starts as draft)
    const baseData = {
      ...originalData,
      internalName: newInternalName,
      name: newInternalName,
      options: { ...originalData.options },
      status: 'draft',
      createdAt: serverTimestamp(),
      hasBeenPlayed: false,
      companyId: companyId,
      duplicatedFrom: campaignId,
      duplicatedAt: serverTimestamp()
    };
    const { _searchInternalName, _searchTitle } = getCampaignSearchFields(baseData);
    const duplicatedData = { ...baseData, _searchInternalName, _searchTitle };

    delete duplicatedData.goLiveDate;
    delete duplicatedData.completeDate;
    delete duplicatedData.publishedAt;
    delete duplicatedData.completedAt;
    delete duplicatedData.pausedAt;
    delete duplicatedData.archivedAt;
    delete duplicatedData.paidAt;
    delete duplicatedData.stripeSessionId;
    
    // Create the new campaign document
    const newCampaignRef = await addDoc(collection(db, 'campaign'), duplicatedData);
    
    console.log(`✅ Campaign duplicated: ${campaignId} → ${newCampaignRef.id}`);
    
    return {
      id: newCampaignRef.id,
      name: newInternalName
    };
  } catch (error) {
    console.error('Error duplicating campaign:', error);
    throw error;
  }
}